import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ExternalLink, Globe, Eye, EyeOff, Save, Lock, UserPlus, Trash2, Crown, CreditCard, AlertTriangle, ArrowLeft, FileText } from "lucide-react";
import FileUpload from "@/components/churchbuilder/FileUpload";
import ActivitiesBlockEditor from "@/components/churchbuilder/blocks/ActivitiesBlockEditor";
import EventsBlockEditor from "@/components/churchbuilder/blocks/EventsBlockEditor";
import GalleryBlockEditor from "@/components/churchbuilder/blocks/GalleryBlockEditor";
import ServiceTimesEditor from "@/components/churchbuilder/blocks/ServiceTimesEditor";
import SeoSettingsBlock from "@/components/churchbuilder/blocks/SeoSettingsBlock";
import PrayerRequestsBlock from "@/components/churchbuilder/blocks/PrayerRequestsBlock";
import SermonsBlock from "@/components/churchbuilder/blocks/SermonsBlock";
import LivestreamBlock from "@/components/churchbuilder/blocks/LivestreamBlock";
import DonationsBlock from "@/components/churchbuilder/blocks/DonationsBlock";
import SupportTicketsBlock from "@/components/churchbuilder/blocks/SupportTicketsBlock";

const STATUS_PILL = {
  draft:     { label: "Draft",     cls: "border-neutral-500/40 text-neutral-300 bg-neutral-800/40" },
  published: { label: "Published", cls: "border-emerald-500/40 text-emerald-300 bg-emerald-900/20" },
  trial:     { label: "Trial",     cls: "border-[#D4AF37]/40 text-[#D4AF37] bg-[#D4AF37]/10" },
  paused:    { label: "Paused",    cls: "border-amber-500/40 text-amber-300 bg-amber-900/20" },
  suspended: { label: "Suspended", cls: "border-red-500/40 text-red-300 bg-red-900/20" },
  expired:   { label: "Expired",   cls: "border-red-500/40 text-red-300 bg-red-900/20" },
};

const fmtBytes = (n) => {
  if (!n && n !== 0) return "—";
  if (n >= 1024 * 1024 * 1024) return `${(n / (1024 ** 3)).toFixed(1)} GB`;
  if (n >= 1024 * 1024) return `${(n / (1024 ** 2)).toFixed(0)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
};

const inp = "w-full bg-black border border-white/15 focus:border-[#D4AF37] outline-none px-3 py-2.5 rounded text-sm text-white";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch { return iso; }
};
const fmtMoney = (amount, currency) => {
  if (amount == null) return "—";
  const n = Number(amount) / 100;
  const sym = (currency || "gbp").toLowerCase() === "gbp" ? "£" : (currency || "").toUpperCase() + " ";
  return `${sym}${n.toFixed(2)}`;
};

function FeatureLock({ visibility, featureLabel }) {
  if (!visibility || visibility.state === "hidden") return null;
  const nextPlanName = visibility.min_plan_name || "Premium";
  return (
    <div className="rounded-xl border border-dashed border-white/15 bg-white/[0.02] p-8 text-center" data-testid="cb-feature-lock">
      <Lock size={20} className="text-neutral-500 mx-auto" />
      <div className="font-serif-display text-lg mt-3">{featureLabel}</div>
      <div className="text-xs text-neutral-400 mt-1">Upgrade to {nextPlanName} to unlock</div>
      <Link to="/church-builder/pricing" className="btn-gold mt-5 inline-flex px-4 py-2 rounded text-[10px] tracking-widest uppercase font-semibold">Upgrade to {nextPlanName}</Link>
    </div>
  );
}

// Convenience: returns one of { renderEnabled, renderLocked, renderHidden }
function FeatureGate({ featureKey, visibility, featureFlags, label, children }) {
  const v = visibility?.[featureKey];
  const state = v?.state || (featureFlags?.[featureKey] ? "enabled" : "locked");
  if (state === "hidden") return null;
  if (state === "enabled") return children;
  return <FeatureLock visibility={v} featureLabel={label} />;
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] tracking-widest uppercase text-neutral-400">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export default function ChurchSlugDashboard() {
  const { user, loading: authLoading } = useAuth();
  const { slug } = useParams();
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isImpersonating = params.get("impersonate") === "1";
  const pathSuggestsBilling = location.pathname.endsWith("/billing");
  const [state, setState] = useState(null);
  const [tab, setTab] = useState(pathSuggestsBilling ? "billing" : "details");
  const [templates, setTemplates] = useState([]);
  const [editors, setEditors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [sub, setSub] = useState(null);
  const [storage, setStorage] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/church-builder/by-slug/${slug}/dashboard`);
      setState(data);
      api.get("/church-builder/templates").then((r) => setTemplates(r.data));
      api.get(`/church-builder/churches/${data.church.id}/subscription`).then((r) => setSub(r.data)).catch(() => {});
      api.get(`/church-builder/churches/${data.church.id}/storage`).then((r) => setStorage(r.data)).catch(() => {});
      if (data.my_role === "church_admin" || data.my_role === "super_admin") {
        api.get(`/church-builder/churches/${data.church.id}/editors`).then((r) => setEditors(r.data));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load dashboard");
      navigate("/church-builder/dashboard");
    }
  }, [slug, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(`/login?next=${encodeURIComponent(`/church-builder/${slug}/dashboard`)}`); return; }
    load();
  }, [user, authLoading, slug, load, navigate]);

  if (authLoading || !user || !state) return <div className="max-w-7xl mx-auto px-6 py-20"><Loader2 className="animate-spin text-[#D4AF37]" /></div>;

  const { church, feature_flags: ff, feature_visibility: fv, plan, trial_expired, my_role } = state;
  const isAdmin = my_role === "church_admin" || my_role === "super_admin";
  const isSuper = my_role === "super_admin";

  const updateChurch = (patch) => setState({ ...state, church: { ...church, ...patch } });

  const saveDetails = async () => {
    setSaving(true);
    try {
      await api.put(`/church-builder/churches/${church.id}`, church);
      toast.success("Details saved");
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const saveContent = async (patch) => {
    try {
      const { data } = await api.put(`/church-builder/churches/${church.id}/content`, { content: patch });
      updateChurch({ content: data.content });
      toast.success("Saved");
    } catch (e) { toast.error("Save failed"); }
  };

  const switchTemplate = async (template_id) => {
    if (!window.confirm("Switch template? Your content is preserved.")) return;
    try {
      await api.put(`/church-builder/churches/${church.id}/template`, { template_id });
      updateChurch({ template_id });
      toast.success("Template switched");
    } catch (e) { toast.error("Switch failed"); }
  };

  const togglePublish = async () => {
    try {
      const url = church.is_published ? "unpublish" : "publish";
      await api.post(`/church-builder/churches/${church.id}/${url}`);
      updateChurch({ is_published: !church.is_published });
      toast.success(church.is_published ? "Site unpublished" : "Site published");
    } catch (e) { toast.error("Action failed"); }
  };

  const inviteEditor = async () => {
    if (!inviteEmail) return;
    try {
      await api.post(`/church-builder/churches/${church.id}/editors`, { email: inviteEmail });
      toast.success(`Editor ${inviteEmail} added`);
      setInviteEmail("");
      const r = await api.get(`/church-builder/churches/${church.id}/editors`);
      setEditors(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Invite failed");
    }
  };

  const removeEditor = async (uid) => {
    if (!window.confirm("Remove this editor?")) return;
    try {
      await api.delete(`/church-builder/churches/${church.id}/editors/${uid}`);
      setEditors((p) => p.filter((x) => x.user_id !== uid));
      toast.success("Removed");
    } catch (e) { toast.error("Remove failed"); }
  };

  const openBillingPortal = async () => {
    try {
      const { data } = await api.post(`/church-builder/churches/${church.id}/billing-portal`, { return_url: window.location.href });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
    }
  };

  return (
    <div data-testid="cb-slug-dashboard" className="max-w-7xl mx-auto px-6 lg:px-10 py-10 text-white">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="label-eyebrow mb-2 flex items-center gap-2">
            Church Builder · Dashboard
            {isSuper && <span className="text-[10px] tracking-widest uppercase text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-0.5 rounded inline-flex items-center gap-1"><Crown size={10} /> Super Admin</span>}
          </div>
          <h1 className="font-serif-display text-3xl">{church.name}</h1>
          <div className="text-xs text-neutral-500 mt-1 flex items-center gap-2 flex-wrap">
            <span>/churches/{church.slug}</span>
            <span>·</span>
            <span>plan: <span className="text-[#D4AF37]">{plan.name}</span></span>
            {church.website_status && STATUS_PILL[church.website_status] && (
              <span data-testid="cb-website-status" className={`text-[10px] tracking-widest uppercase border px-2 py-0.5 rounded ${STATUS_PILL[church.website_status].cls}`}>
                {STATUS_PILL[church.website_status].label}
              </span>
            )}
            {storage && (
              <span data-testid="cb-storage-indicator" className="text-[10px] tracking-widest uppercase text-neutral-500 border border-white/10 px-2 py-0.5 rounded">
                Storage {fmtBytes(storage.used_bytes)} / {fmtBytes(storage.limit_bytes)} ({storage.percent}%)
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={`/churches/${church.slug}${church.is_published ? "" : "?preview=1"}`} target="_blank" rel="noreferrer" data-testid="cb-preview-btn" className="btn-ghost-gold px-3 py-2 rounded text-xs tracking-widest uppercase inline-flex items-center gap-2"><Globe size={13} /> {church.is_published ? "View Site" : "Preview Site"} <ExternalLink size={11} /></a>
          {isAdmin && (
            <button data-testid="cb-toggle-publish" onClick={togglePublish} className={`px-3 py-2 rounded text-xs tracking-widest uppercase inline-flex items-center gap-2 font-semibold ${church.is_published ? "btn-ghost-gold" : "btn-gold"}`}>
              {church.is_published ? <><EyeOff size={13} /> Unpublish</> : <><Eye size={13} /> Publish</>}
            </button>
          )}
        </div>
      </div>

      {isSuper && isImpersonating && (
        <div data-testid="cb-impersonate-banner" className="mt-6 rounded-md border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#FFEBA8] px-4 py-3 text-sm flex items-center justify-between gap-3 flex-wrap">
          <span className="inline-flex items-center gap-2"><Crown size={14} className="text-[#D4AF37]" /> You are currently acting as <strong className="text-white">{church.name}</strong> · all changes are real.</span>
          <button
            type="button"
            data-testid="cb-impersonate-return"
            onClick={() => navigate("/church-builder/admin")}
            className="btn-gold px-3 py-1.5 rounded text-[10px] tracking-widest uppercase font-semibold inline-flex items-center gap-2"
          >
            <ArrowLeft size={12} /> Return to Super Admin
          </button>
        </div>
      )}

      {trial_expired && !isSuper && (
        <div data-testid="cb-trial-banner" className="mt-6 rounded-md border border-amber-500/40 bg-amber-900/20 text-amber-200 px-4 py-3 text-sm flex items-center gap-2">
          <Lock size={14} /> Your 7-day trial has ended. <Link to="/church-builder/pricing" className="underline ml-1">Upgrade</Link> to keep your site live and admin features enabled.
        </div>
      )}

      {sub?.last_payment_status === "failed" && (
        <div data-testid="cb-payment-failed-banner" className="mt-4 rounded-md border border-red-500/40 bg-red-900/20 text-red-200 px-4 py-3 text-sm flex items-center justify-between gap-3 flex-wrap">
          <span className="inline-flex items-center gap-2"><AlertTriangle size={14} /> Last subscription payment failed. Please update your card to avoid losing access.</span>
          <button onClick={openBillingPortal} data-testid="cb-update-card-btn" className="btn-gold px-3 py-1.5 rounded text-[10px] tracking-widest uppercase font-semibold">Update Card</button>
        </div>
      )}

      <div className="flex gap-2 mt-8 border-b border-white/10 overflow-x-auto">
        {[
          ["details", "Details", true],
          ["content", "Content", true],
          ["activities", "Activities", true],
          ["events", "Events", true],
          ["gallery", "Gallery", true],
          ["prayer", "Prayer", ff?.prayer_form],
          ["sermons", "Sermons", ff?.sermons],
          ["livestream", "Livestream", ff?.livestream],
          ["donations", "Donations", ff?.donations],
          ["support", "Support", true],
          ["seo", "SEO", true],
          ["template", "Template", true],
          ["editors", "Editors", isAdmin],
          ["billing", "Subscription", isAdmin],
          ["admin", "Super Admin", isSuper],
        ].filter(([_, __, ok]) => ok).map(([k, l]) => (
          <button key={k} data-testid={`cb-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-3 text-xs tracking-widest uppercase whitespace-nowrap border-b-2 ${tab === k ? "text-[#D4AF37] border-[#D4AF37]" : "text-neutral-400 border-transparent hover:text-white"}`}>{l}</button>
        ))}
      </div>

      {tab === "details" && (
        <div className="mt-8 max-w-3xl space-y-5">
          <Field label="Public URL"><div className="text-sm text-neutral-300">{window.location.origin}/churches/<span className="text-[#D4AF37]">{church.slug}</span></div></Field>
          <Field label="Church Name"><input value={church.name || ""} onChange={(e) => updateChurch({ name: e.target.value })} className={inp} data-testid="cb-d-name" /></Field>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Denomination"><input value={church.denomination || ""} onChange={(e) => updateChurch({ denomination: e.target.value })} className={inp} /></Field>
            <Field label="Country"><input value={church.country || ""} onChange={(e) => updateChurch({ country: e.target.value })} className={inp} /></Field>
            <Field label="Pastor Name"><input value={church.pastor_name || ""} onChange={(e) => updateChurch({ pastor_name: e.target.value })} className={inp} /></Field>
            <Field label="Phone"><input value={church.phone || ""} onChange={(e) => updateChurch({ phone: e.target.value })} className={inp} /></Field>
            <Field label="Email"><input type="email" value={church.email || ""} onChange={(e) => updateChurch({ email: e.target.value })} className={inp} /></Field>
            <Field label="External Website"><input value={church.website || ""} onChange={(e) => updateChurch({ website: e.target.value })} className={inp} /></Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Address Line 1"><input value={church.address || ""} onChange={(e) => updateChurch({ address: e.target.value })} className={inp} placeholder="1 Church Street" /></Field>
            <Field label="Address Line 2"><input value={church.address_line2 || ""} onChange={(e) => updateChurch({ address_line2: e.target.value })} className={inp} /></Field>
            <Field label="City"><input value={church.city || ""} onChange={(e) => updateChurch({ city: e.target.value })} className={inp} /></Field>
            <Field label="Postcode"><input value={church.postcode || ""} onChange={(e) => updateChurch({ postcode: e.target.value })} className={inp} /></Field>
          </div>

          <div className="grid sm:grid-cols-3 gap-5">
            <FileUpload churchId={church.id} value={church.logo_url} onChange={(url) => updateChurch({ logo_url: url })} label="Logo" testid="cb-upload-logo" />
            <FileUpload churchId={church.id} value={church.hero_image_url} onChange={(url) => updateChurch({ hero_image_url: url })} label="Hero image" testid="cb-upload-hero" />
            <FileUpload churchId={church.id} value={church.pastor_photo_url} onChange={(url) => updateChurch({ pastor_photo_url: url })} label="Pastor photo" testid="cb-upload-pastor" />
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Primary Color"><input type="color" value={church.brand_primary_color || "#D4AF37"} onChange={(e) => updateChurch({ brand_primary_color: e.target.value })} className="h-10 w-full bg-black border border-white/15 rounded" /></Field>
            <Field label="Accent Color"><input type="color" value={church.brand_accent_color || "#0A0A0A"} onChange={(e) => updateChurch({ brand_accent_color: e.target.value })} className="h-10 w-full bg-black border border-white/15 rounded" /></Field>
          </div>
          <button data-testid="cb-save-details" onClick={saveDetails} disabled={saving} className="btn-gold px-6 py-3 rounded text-xs tracking-widest uppercase font-semibold inline-flex items-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Details
          </button>
        </div>
      )}

      {tab === "content" && (
        <div className="mt-8 max-w-4xl space-y-6">
          <div className="luxury-card rounded-xl p-5 space-y-4">
            <Field label="Welcome Message"><textarea rows={3} value={church.content?.welcome_message || ""} onChange={(e) => updateChurch({ content: { ...church.content, welcome_message: e.target.value } })} onBlur={() => saveContent({ welcome_message: church.content?.welcome_message || "" })} className={inp} data-testid="cb-c-welcome" /></Field>
            <Field label="Pastor Bio"><textarea rows={4} value={church.content?.pastor_bio || ""} onChange={(e) => updateChurch({ content: { ...church.content, pastor_bio: e.target.value } })} onBlur={() => saveContent({ pastor_bio: church.content?.pastor_bio || "" })} className={inp} /></Field>
            <FeatureGate featureKey="livestream" visibility={fv} featureFlags={ff} label="Livestream URL">
              <Field label="YouTube Livestream URL"><input value={church.content?.livestream_url || ""} onChange={(e) => updateChurch({ content: { ...church.content, livestream_url: e.target.value } })} onBlur={() => saveContent({ livestream_url: church.content?.livestream_url || "" })} className={inp} placeholder="https://..." /></Field>
            </FeatureGate>
            <div className="grid sm:grid-cols-3 gap-3">
              {["youtube", "facebook", "instagram"].map((s) => (
                <Field key={s} label={s.charAt(0).toUpperCase() + s.slice(1)}>
                  <input value={church.content?.social?.[s] || ""} onChange={(e) => updateChurch({ content: { ...church.content, social: { ...(church.content?.social || {}), [s]: e.target.value } } })} onBlur={() => saveContent({ social: church.content?.social || {} })} className={inp} />
                </Field>
              ))}
            </div>
          </div>

          <FeatureGate featureKey="basic_service_times" visibility={fv} featureFlags={ff} label="Service Times">
            <ServiceTimesEditor items={church.content?.service_times || []} onChange={(arr) => saveContent({ service_times: arr })} />
          </FeatureGate>

          <FeatureGate featureKey="departments" visibility={fv} featureFlags={ff} label="Departments">
            <DepartmentsBlock items={church.content?.departments || []} onChange={(arr) => saveContent({ departments: arr })} />
          </FeatureGate>
        </div>
      )}

      {tab === "activities" && (
        <div className="mt-8 max-w-4xl">
          <FeatureGate featureKey="basic_events" visibility={fv} featureFlags={ff} label="Weekly Activities">
            <ActivitiesBlockEditor items={church.content?.weekly_activities || []} onChange={(arr) => saveContent({ weekly_activities: arr })} />
          </FeatureGate>
        </div>
      )}

      {tab === "events" && (
        <div className="mt-8 max-w-4xl">
          <FeatureGate featureKey="enhanced_events" visibility={fv} featureFlags={ff} label="Events">
            <EventsBlockEditor churchId={church.id} items={church.content?.events || []} onChange={(arr) => saveContent({ events: arr })} />
          </FeatureGate>
        </div>
      )}

      {tab === "gallery" && (
        <div className="mt-8 max-w-4xl">
          <FeatureGate featureKey="gallery" visibility={fv} featureFlags={ff} label="Gallery">
            <GalleryBlockEditor churchId={church.id} items={church.content?.gallery || []} onChange={(arr) => saveContent({ gallery: arr })} />
          </FeatureGate>
        </div>
      )}

      {tab === "prayer" && (
        <div className="mt-8 max-w-4xl">
          <FeatureGate featureKey="prayer_form" visibility={fv} featureFlags={ff} label="Prayer Requests">
            <PrayerRequestsBlock churchId={church.id} />
          </FeatureGate>
        </div>
      )}

      {tab === "sermons" && (
        <div className="mt-8 max-w-4xl">
          <FeatureGate featureKey="sermons" visibility={fv} featureFlags={ff} label="Sermon Library">
            <SermonsBlock churchId={church.id} />
          </FeatureGate>
        </div>
      )}

      {tab === "livestream" && (
        <div className="mt-8 max-w-4xl">
          <FeatureGate featureKey="livestream" visibility={fv} featureFlags={ff} label="Livestream">
            <LivestreamBlock churchId={church.id} />
          </FeatureGate>
        </div>
      )}

      {tab === "donations" && (
        <div className="mt-8 max-w-4xl">
          <FeatureGate featureKey="donations" visibility={fv} featureFlags={ff} label="Donations">
            <DonationsBlock church={church} />
          </FeatureGate>
        </div>
      )}

      {tab === "support" && (
        <div className="mt-8 max-w-4xl">
          <SupportTicketsBlock church={church} />
        </div>
      )}

      {tab === "seo" && (
        <div className="mt-8 max-w-3xl">
          <SeoSettingsBlock
            churchId={church.id}
            church={church}
            onChange={(next) => setState({ ...state, church: next })}
            onSave={async (next) => {
              try { await api.put(`/church-builder/churches/${church.id}`, next); toast.success("SEO saved"); }
              catch (e) { toast.error("SEO save failed"); }
            }}
          />
        </div>
      )}

      {tab === "template" && (
        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {templates.map((t) => (
            <div key={t.id} className={`luxury-card rounded-xl overflow-hidden ${church.template_id === t.id ? "ring-2 ring-[#D4AF37]" : ""}`}>
              <img src={t.preview_image_url} alt={t.name} className="w-full aspect-[4/3] object-cover" />
              <div className="p-4">
                <div className="font-serif-display text-lg">{t.name}</div>
                <div className="text-xs text-neutral-400 mt-1">{t.tagline}</div>
                {church.template_id === t.id ? <div className="mt-3 text-[10px] tracking-widest uppercase text-[#D4AF37]">● Current</div> : t.available ? (
                  <button data-testid={`cb-switch-${t.id}`} onClick={() => switchTemplate(t.id)} className="btn-ghost-gold mt-3 w-full px-3 py-2 rounded text-[10px] tracking-widest uppercase">Switch to this</button>
                ) : <div className="mt-3 text-[10px] tracking-widest uppercase text-neutral-500">Coming soon</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "editors" && (
        <div className="mt-8 max-w-3xl" data-testid="cb-editors-tab">
          <div className="luxury-card rounded-xl p-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="font-serif-display text-xl">Team</div>
                <div className="text-xs text-neutral-400 mt-1">
                  {plan.max_editors === 0 ? "Unlimited editors" : `Up to ${plan.max_editors} editor${plan.max_editors === 1 ? "" : "s"}`} on {plan.name} plan.
                  · {editors.filter((e) => e.role === "church_editor").length} active.
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-5">
              <input data-testid="cb-invite-email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="editor@email.com" className={inp} type="email" />
              <button data-testid="cb-invite-submit" onClick={inviteEditor} className="btn-gold px-4 py-2 rounded text-xs tracking-widest uppercase font-semibold inline-flex items-center gap-2"><UserPlus size={13} /> Invite</button>
            </div>
            <p className="text-[10px] text-neutral-500 mt-2">New email? We&apos;ll create the account and email them a temporary password automatically.</p>

            <div className="mt-6 space-y-2">
              {editors.map((e) => (
                <div key={e.id} data-testid={`cb-editor-${e.email}`} className="flex items-center justify-between border border-white/5 rounded px-4 py-3">
                  <div>
                    <div className="text-sm">{e.name || "—"} <span className="text-[10px] tracking-widest text-neutral-500 ml-2">{e.role}</span></div>
                    <div className="text-xs text-neutral-400">{e.email}</div>
                  </div>
                  {e.role === "church_editor" && (
                    <button data-testid={`cb-remove-editor-${e.email}`} onClick={() => removeEditor(e.user_id)} className="text-neutral-400 hover:text-red-400"><Trash2 size={14} /></button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "billing" && (
        <BillingTab church={church} sub={sub} plan={plan} slug={slug} onPortal={openBillingPortal} />
      )}

      {tab === "admin" && isSuper && (
        <SuperAdminTab church={church} sub={sub} onUpdate={updateChurch} reload={load} />
      )}
    </div>
  );
}

// ----- Smaller list blocks (Departments / Sermons kept simple) -----
function DepartmentsBlock({ items, onChange }) {
  const update = (i, p) => { const a = [...items]; a[i] = { ...a[i], ...p }; onChange(a); };
  const add = () => onChange([...items, { name: "", lead: "", description: "" }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="luxury-card rounded-xl p-5" data-testid="block-departments">
      <div className="flex items-center justify-between">
        <div className="font-serif-display text-xl">Departments</div>
        <button onClick={add} className="btn-ghost-gold text-[10px] tracking-widest uppercase px-3 py-1.5 rounded">+ Add</button>
      </div>
      <div className="mt-4 space-y-3">
        {items.map((row, i) => (
          <div key={i} className="border border-white/5 rounded p-3 bg-black/30 space-y-2">
            <input className={inp} placeholder="Department name" value={row.name || ""} onChange={(e) => update(i, { name: e.target.value })} />
            <input className={inp} placeholder="Lead" value={row.lead || ""} onChange={(e) => update(i, { lead: e.target.value })} />
            <textarea className={inp} placeholder="Description" rows={2} value={row.description || ""} onChange={(e) => update(i, { description: e.target.value })} />
            <button onClick={() => remove(i)} className="text-red-400 text-xs tracking-widest uppercase">Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SermonsLegacyRemoved() {
  // Kept as a placeholder — legacy inline sermon editor was replaced by
  // the structured SermonsBlock component (imported above).
  return null;
}
SermonsLegacyRemoved.displayName = "SermonsLegacyRemoved";

// ----- Billing tab (real subscription details + portal) -----
function BillingTab({ church, sub, plan, slug, onPortal }) {
  const status = sub?.subscription_status;
  const showCheckout = !status || status === "incomplete";

  // Trial-days-remaining (counted from now → trial_end_at)
  const trialEnd = sub?.trial_end_at || church.trial_ends_at;
  let trialDaysRemaining = null;
  if (trialEnd) {
    const ms = new Date(trialEnd).getTime() - Date.now();
    trialDaysRemaining = ms > 0 ? Math.ceil(ms / (1000 * 60 * 60 * 24)) : 0;
  }

  // Invoices (Stripe)
  const [invoices, setInvoices] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  useEffect(() => {
    if (!church?.id || !sub?.stripe_customer_id) return;
    setInvLoading(true);
    api.get(`/church-builder/churches/${church.id}/invoices`)
      .then((r) => setInvoices(r.data?.items || []))
      .catch(() => {})
      .finally(() => setInvLoading(false));
  }, [church?.id, sub?.stripe_customer_id]);

  const cancelSubscription = () => {
    if (!window.confirm(
      "Cancel your subscription at the end of the current billing period?\n\n" +
      "You'll keep access until then and won't be charged again."
    )) return;
    // Stripe's hosted Billing Portal handles cancel-at-period-end with a single click.
    onPortal();
  };

  return (
    <div className="mt-8 max-w-3xl space-y-6" data-testid="cb-billing-tab">
      <div className="luxury-card rounded-xl p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow">Current Plan</div>
            <div className="font-serif-display text-3xl mt-2" data-testid="cb-billing-plan-name">{plan.name}</div>
          </div>
          {trialDaysRemaining != null && status === "trialing" && (
            <div data-testid="cb-billing-trial-days" className="rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-3 text-center">
              <div className="text-[10px] tracking-widest uppercase text-[#D4AF37]">Trial days remaining</div>
              <div className="font-serif-display text-3xl text-[#D4AF37]">{trialDaysRemaining}</div>
            </div>
          )}
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
          <Cell label="Subscription status" value={status ? status.replace("_", " ") : "no subscription"} testid="cb-billing-status" />
          <Cell label="Billing" value={sub?.subscription_amount != null ? `${fmtMoney(sub.subscription_amount, sub.subscription_currency)}${sub?.subscription_interval === "annual" ? "/yr" : "/mo"}` : "—"} testid="cb-billing-amount" />
          <Cell label="Trial ends" value={fmtDate(trialEnd)} testid="cb-billing-trial" />
          <Cell label="Next billing date" value={fmtDate(sub?.next_billing_at)} testid="cb-billing-next" />
          <Cell label="Last payment" value={(sub?.last_payment_status || "—").replace("_", " ")} testid="cb-billing-last" />
          <Cell label="Payment method" value={sub?.stripe_customer_id ? "On file (managed by Stripe)" : "Not yet added"} testid="cb-billing-payment-method" />
        </div>

        <div className="mt-7 flex flex-wrap gap-3 items-center">
          {showCheckout ? (
            <Link to={`/church-builder/${slug}/subscribe`} data-testid="cb-billing-upgrade" className="btn-gold inline-flex px-5 py-2.5 rounded text-xs tracking-widest uppercase font-semibold">Upgrade Plan & Enter Card</Link>
          ) : (
            <>
              <Link to="/church-builder/pricing" data-testid="cb-billing-change-plan" className="btn-gold inline-flex px-5 py-2.5 rounded text-xs tracking-widest uppercase font-semibold">Upgrade / Change Plan</Link>
              <button onClick={onPortal} data-testid="cb-billing-portal" className="btn-ghost-gold inline-flex px-5 py-2.5 rounded text-xs tracking-widest uppercase gap-2 items-center"><CreditCard size={14} /> Manage Payment Method</button>
              <button onClick={cancelSubscription} data-testid="cb-billing-cancel" className="text-xs tracking-widest uppercase text-red-300 hover:text-red-200 px-3 py-2.5">Cancel Subscription</button>
            </>
          )}
        </div>
      </div>

      {sub?.stripe_customer_id && (
        <div className="luxury-card rounded-xl p-6" data-testid="cb-billing-invoices">
          <div className="flex items-center justify-between">
            <div className="font-serif-display text-xl flex items-center gap-2"><FileText size={16} className="text-[#D4AF37]" /> Invoices</div>
            <div className="text-[10px] tracking-widest uppercase text-neutral-500">Most recent first</div>
          </div>
          {invLoading ? (
            <div className="mt-4 text-xs text-neutral-500"><Loader2 className="inline animate-spin mr-2" size={12} /> Loading…</div>
          ) : invoices.length === 0 ? (
            <div className="mt-4 text-xs text-neutral-500">No invoices yet — your first charge will appear here once the trial ends.</div>
          ) : (
            <table className="w-full text-xs mt-4">
              <thead><tr className="border-b border-white/10 text-[10px] tracking-widest uppercase text-neutral-500">
                <th className="text-left py-2">Date</th>
                <th className="text-left">Invoice #</th>
                <th className="text-right">Amount</th>
                <th className="text-left">Status</th>
                <th className="text-right">Download</th>
              </tr></thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-white/5" data-testid={`cb-billing-invoice-${inv.id}`}>
                    <td className="py-2 text-neutral-300">{inv.created ? new Date(inv.created * 1000).toLocaleDateString() : "—"}</td>
                    <td className="text-neutral-400">{inv.number || inv.id.slice(0, 14)}</td>
                    <td className="text-right text-[#D4AF37]">{fmtMoney(inv.amount_paid || inv.amount_due, inv.currency)}</td>
                    <td className="text-neutral-400 capitalize">{(inv.status || "").replace("_", " ")}</td>
                    <td className="text-right">
                      {inv.invoice_pdf ? (
                        <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" data-testid={`cb-billing-invoice-pdf-${inv.id}`} className="text-[#D4AF37] hover:opacity-80 inline-flex items-center gap-1">PDF <ExternalLink size={11} /></a>
                      ) : inv.hosted_invoice_url ? (
                        <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-[#D4AF37] hover:opacity-80 inline-flex items-center gap-1">View <ExternalLink size={11} /></a>
                      ) : <span className="text-neutral-600">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function Cell({ label, value, testid }) {
  return (
    <div className="border-b border-white/5 pb-2" data-testid={testid}>
      <div className="text-[10px] tracking-widest uppercase text-neutral-500">{label}</div>
      <div className="text-white mt-1 capitalize">{value}</div>
    </div>
  );
}

function SuperAdminTab({ church, sub, onUpdate, reload }) {
  const [newSlug, setNewSlug] = useState(church.slug);
  const [newPlan, setNewPlan] = useState(church.subscription_plan);
  const [extendDays, setExtendDays] = useState(7);

  const renameSlug = async () => {
    try {
      await api.patch(`/admin/church-builder/churches/${church.id}/slug`, { slug: newSlug });
      toast.success("Slug updated");
      window.location.href = `/church-builder/${newSlug}/dashboard`;
    } catch (e) { toast.error(e?.response?.data?.detail || "Slug change failed"); }
  };

  const changePlan = async () => {
    try {
      const body = { subscription_plan: newPlan };
      if (newPlan === "trial" && extendDays) body.extend_trial_days = extendDays;
      await api.patch(`/admin/church-builder/churches/${church.id}/plan`, body);
      toast.success("Plan updated");
      reload();
    } catch (e) { toast.error("Plan update failed"); }
  };

  const syncSub = async () => {
    try {
      await api.post(`/admin/church-builder/churches/${church.id}/subscription/sync`);
      toast.success("Subscription synced from Stripe");
      reload();
    } catch (e) { toast.error("Sync failed"); }
  };

  const cancelSub = async () => {
    if (!window.confirm("Cancel this church's subscription at the end of the current period?")) return;
    try {
      await api.post(`/admin/church-builder/churches/${church.id}/subscription/cancel`);
      toast.success("Subscription scheduled to cancel");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  const reactivateSub = async () => {
    try {
      await api.post(`/admin/church-builder/churches/${church.id}/subscription/reactivate`);
      toast.success("Subscription reactivated");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reactivate failed"); }
  };

  return (
    <div className="mt-8 max-w-3xl space-y-5" data-testid="cb-admin-tab">
      <div className="luxury-card rounded-xl p-5">
        <div className="font-serif-display text-xl flex items-center gap-2"><Crown size={16} className="text-[#D4AF37]" /> Super Admin Controls</div>
        <p className="text-xs text-neutral-400 mt-1">Only visible to HOHVibes super admins.</p>

        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <Field label="Slug (URL path)">
            <div className="flex gap-2">
              <input data-testid="cb-admin-slug" value={newSlug} onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} className={inp} />
              <button data-testid="cb-admin-rename-slug" onClick={renameSlug} className="btn-ghost-gold px-3 py-2 rounded text-[10px] tracking-widest uppercase">Apply</button>
            </div>
          </Field>
          <Field label="Subscription Plan (manual override)">
            <div className="flex gap-2">
              <select data-testid="cb-admin-plan" value={newPlan} onChange={(e) => setNewPlan(e.target.value)} className={inp}>
                <option value="trial">trial</option>
                <option value="starter">starter</option>
                <option value="standard">standard</option>
                <option value="premium">premium</option>
              </select>
              <button data-testid="cb-admin-change-plan" onClick={changePlan} className="btn-gold px-3 py-2 rounded text-[10px] tracking-widest uppercase font-semibold">Apply</button>
            </div>
            {newPlan === "trial" && (
              <input type="number" min="0" max="90" data-testid="cb-admin-extend-trial" value={extendDays} onChange={(e) => setExtendDays(e.target.value)} className={inp + " mt-2 text-xs"} placeholder="Extend trial by N days" />
            )}
          </Field>
        </div>
      </div>

      <div className="luxury-card rounded-xl p-5" data-testid="cb-admin-stripe-panel">
        <div className="font-serif-display text-xl">Stripe Subscription</div>
        <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
          <Cell label="Status" value={(sub?.subscription_status || "—").replace("_", " ")} testid="cb-admin-sub-status" />
          <Cell label="Plan" value={sub?.subscription_plan || "—"} testid="cb-admin-sub-plan" />
          <Cell label="Interval" value={sub?.subscription_interval || "—"} />
          <Cell label="Billing" value={sub?.subscription_amount != null ? fmtMoney(sub.subscription_amount, sub.subscription_currency) : "—"} />
          <Cell label="Trial start" value={fmtDate(sub?.trial_start_at)} testid="cb-admin-trial-start" />
          <Cell label="Trial end" value={fmtDate(sub?.trial_end_at)} testid="cb-admin-trial-end" />
          <Cell label="Next billing" value={fmtDate(sub?.next_billing_at)} testid="cb-admin-next-billing" />
          <Cell label="Last payment" value={(sub?.last_payment_status || "—").replace("_", " ")} testid="cb-admin-last-payment" />
          <Cell label="Stripe customer" value={sub?.stripe_customer_id || "—"} />
          <Cell label="Stripe subscription" value={sub?.stripe_subscription_id || "—"} />
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <button onClick={syncSub} data-testid="cb-admin-sync" className="btn-ghost-gold px-3 py-2 rounded text-[10px] tracking-widest uppercase">Sync from Stripe</button>
          {sub?.subscription_status && sub.subscription_status !== "canceled" && (
            <button onClick={cancelSub} data-testid="cb-admin-cancel" className="btn-ghost-gold px-3 py-2 rounded text-[10px] tracking-widest uppercase text-red-300 border-red-500/40">Cancel at period end</button>
          )}
          {sub?.cancel_at_period_end && (
            <button onClick={reactivateSub} data-testid="cb-admin-reactivate" className="btn-gold px-3 py-2 rounded text-[10px] tracking-widest uppercase font-semibold">Reactivate</button>
          )}
        </div>
      </div>

      <div className="text-[10px] text-neutral-500 mt-2 tracking-widest uppercase">Owner: {church.owner_email}</div>
    </div>
  );
}
